import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
};

export const SubscriptionProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [entitlement, setEntitlement] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPlans = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/subscription/plans`);
      setPlans(data.plans || []);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setEntitlement(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/subscription/status`);
      setEntitlement(data);
      return data;
    } catch (e) {
      setEntitlement({ is_premium: false, status: 'free' });
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { loadPlans(); }, [loadPlans]);
  useEffect(() => {
    if (!authLoading) refresh();
  }, [authLoading, isAuthenticated, refresh]);

  const startCheckout = useCallback(async (lookupKey) => {
    const { data } = await axios.post(`${API}/subscription/checkout`, {
      lookup_key: lookupKey,
      origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  }, []);

  const openPortal = useCallback(async () => {
    const { data } = await axios.post(`${API}/subscription/portal`, {
      origin_url: window.location.origin,
    });
    window.location.href = data.portal_url;
  }, []);

  const value = {
    entitlement,
    isPremium: !!entitlement?.is_premium,
    status: entitlement?.status || 'free',
    plans,
    loading: loading || authLoading,
    refresh,
    startCheckout,
    openPortal,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
